import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

/**
 * Advanced Settings Step - Expert configuration options
 */
export default function AdvancedSettingsStep({
  inputs,
  updateInput,
  monteCarloScenarios,
  setMonteCarloScenarios
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Advanced Settings</CardTitle>
        <CardDescription>
          Fine-tune investment assumptions and simulation parameters. These defaults are suitable for most users.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Investment Parameters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="inflationRate">Inflation Rate (%)</Label>
            <Input
              id="inflationRate"
              type="number"
              value={inputs.inflationRate}
              onChange={(e) => updateInput('inflationRate', parseFloat(e.target.value))}
              min="0"
              max="10"
              step="0.1"
            />
            <p className="text-xs text-muted-foreground">
              Historical average: 2-3%
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="expectedReturn">Expected Return (%)</Label>
            <Input
              id="expectedReturn"
              type="number"
              value={inputs.expectedReturn}
              onChange={(e) => updateInput('expectedReturn', parseFloat(e.target.value))}
              min="0"
              max="20"
              step="0.1"
            />
            <p className="text-xs text-muted-foreground">
              Typical balanced portfolio: 6-8%
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="volatility">Volatility (%)</Label>
            <Input
              id="volatility"
              type="number"
              value={inputs.volatility}
              onChange={(e) => updateInput('volatility', parseFloat(e.target.value))}
              min="0"
              max="50"
              step="0.1"
            />
            <p className="text-xs text-muted-foreground">
              Standard deviation of returns
            </p>
          </div>
        </div>

        {/* Risk Profile & Monte Carlo */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="riskProfile">Risk Profile</Label>
            <Select value={inputs.riskProfile} onValueChange={(value) => updateInput('riskProfile', value)}>
              <SelectTrigger id="riskProfile">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="conservative">Conservative (Lower risk, lower returns)</SelectItem>
                <SelectItem value="moderate">Moderate (Balanced approach)</SelectItem>
                <SelectItem value="aggressive">Aggressive (Higher risk, higher returns)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Adjusts asset allocation over time
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="monteCarloScenarios">Monte Carlo Scenarios</Label>
            <Select
              value={monteCarloScenarios.toString()}
              onValueChange={(value) => setMonteCarloScenarios(parseInt(value))}
            >
              <SelectTrigger id="monteCarloScenarios">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="500">500 (Fast - for quick testing)</SelectItem>
                <SelectItem value="1000">1,000 (Standard - recommended)</SelectItem>
                <SelectItem value="5000">5,000 (Detailed - more accurate)</SelectItem>
                <SelectItem value="10000">10,000 (Comprehensive - best accuracy)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              More scenarios = more accurate probability estimates
            </p>
          </div>
        </div>

        {/* Information Alert */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">Understanding Advanced Settings</h4>
          <ul className="text-xs text-blue-800 space-y-1">
            <li>• <strong>Inflation Rate:</strong> Erodes purchasing power over time</li>
            <li>• <strong>Expected Return:</strong> Average annual portfolio growth rate</li>
            <li>• <strong>Volatility:</strong> How much returns fluctuate year-to-year</li>
            <li>• <strong>Risk Profile:</strong> Determines asset allocation glide path</li>
            <li>• <strong>Monte Carlo:</strong> Simulates thousands of possible market scenarios</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
